import { Facepile, IFacepilePersona } from '@fluentui/react/lib/Facepile';
import AppContext from 'App/AppContext';
import { IPIconButton, IPText } from 'baseComponents';
import { IPPersonaSize } from 'baseComponents/Persona/IPPersona';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { ApprovalCallOut } from 'components/Approval/ApprovalCallOut';
import { LinkPublicationHistoryModal } from 'components/Publication/LinkPublicationHistoryModal';
import { Approval, ApprovalDetailStatus, ApprovalMainStatus, ApprovalState } from 'models/approval';
import ResourceLink from 'models/resourceLink';
import { Fragment, useContext, useEffect, useMemo, useState } from 'react';
import { toLocaleDateShort } from 'utils/datetime';
import { sortOnString } from 'utils/sorting';
import { IconStatusApproved, IconStatusConcept, IconStatusPending, IconStatusRejected } from './ApprovalHelpers';

interface ILatestApproval {
  approvals: Approval[] | undefined;
  link: ResourceLink;
  showHistoryLink?: boolean;
}

export const LatestApproval = (props: ILatestApproval) => {
  const appContext = useContext(AppContext);
  const [isCalloutVisible, setIsCalloutVisible] = useState<boolean>(false);
  const [isHistoryVisible, setIsHistoryVisible] = useState<boolean>(false);
  const [mainStatus, setMainStatus] = useState<ApprovalMainStatus | undefined>(undefined);
  const [statusIcon, setStatusIcon] = useState<JSX.Element>(<Fragment></Fragment>);
  const [facepilePersonas, setFacepilePersonas] = useState<IFacepilePersona[]>();
  const [details, setDetails] = useState<ApprovalDetailStatus[]>([]);

  useEffect(() => {
    const setApprovalIcon = (apprState: ApprovalState) => {
      switch (apprState) {
        case ApprovalState.Approved:
          return setStatusIcon(
            <IPIconButton
              id={props.link.linkId.toString()}
              styles={{ root: { height: 22 } }}
              iconProps={IconStatusApproved}
            />,
          );
        case ApprovalState.Rejected:
          return setStatusIcon(
            <IPIconButton
              id={props.link.linkId.toString()}
              styles={{ root: { height: 22 } }}
              iconProps={IconStatusRejected}
            />,
          );
        case ApprovalState.Pending:
          return setStatusIcon(
            <IPIconButton
              id={props.link.linkId.toString()}
              styles={{ root: { height: 22 } }}
              iconProps={IconStatusPending}
            />,
          );
        case ApprovalState.Concept:
          return setStatusIcon(
            <IPIconButton
              id={props.link.linkId.toString()}
              styles={{ root: { height: 22 } }}
              iconProps={IconStatusConcept}
            />,
          );
      }
    };

    if (props.approvals && props.approvals.length > 0) {
      const approval = props.approvals[0];
      const mainStatus = approval.getMainStatus(appContext.globalDataCache);
      const userIds = approval.getUniqueUsers();
      const details = approval.getDetailStatus();

      details.sort((a, b) => {
        const user1 = appContext.globalDataCache.users.get(a.userId);
        const user2 = appContext.globalDataCache.users.get(b.userId);

        return user1.name.localeCompare(user2.name);
      });
      const facePiles: IFacepilePersona[] = [];

      setMainStatus(mainStatus);
      setDetails(details);
      setApprovalIcon(mainStatus.status);

      //set face piles
      userIds?.forEach((userId) => {
        let name: string = '';
        if (userId) {
          name = appContext.globalDataCache.users.get(userId).name;
        }
        facePiles.push({ personaName: name });
      });
      facePiles.sort((a, b) => sortOnString(a.personaName, b.personaName));
      setFacepilePersonas(facePiles);
    }
  }, [appContext.globalDataCache, appContext.globalDataCache.users, props.approvals, props.link]);

  const targetId = useMemo(() => `latest-approval-${props.link.linkId}`, [props.link.linkId]);

  const openHistory = () => {
    setIsHistoryVisible(true);
  };

  //
  // Main render
  //
  if (!props.approvals || props.approvals.length === 0) {
    return null;
  } else {
    return (
      <IPStack
        id={targetId}
        onMouseOver={() => {
          setIsCalloutVisible(true);
        }}
        onMouseLeave={() => {
          setIsCalloutVisible(false);
        }}
      >
        <IPStack.Item>
          <IPStack horizontal verticalAlign="center">
            <IPStack.Item>{statusIcon}</IPStack.Item>
            <IPStack.Item shrink={false}>
              <IPText>{toLocaleDateShort(mainStatus?.date)}</IPText>
            </IPStack.Item>
            {facepilePersonas && (
              <IPStack.Item styles={{ root: { paddingLeft: 10 } }}>
                <Facepile personaSize={IPPersonaSize.size24} personas={facepilePersonas!} />
              </IPStack.Item>
            )}
          </IPStack>
          {isCalloutVisible && (
            <ApprovalCallOut
              targetId={targetId}
              approvalMain={mainStatus}
              approvalDetails={details}
              isVisible={isCalloutVisible}
              onOpenHistory={props.showHistoryLink ? openHistory : undefined}
            />
          )}
        </IPStack.Item>
        {isHistoryVisible && (
          <LinkPublicationHistoryModal
            isOpen={isHistoryVisible}
            link={props.link}
            onClose={() => setIsHistoryVisible(false)}
          />
        )}
      </IPStack>
    );
  }
};
